import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, MICHAEL_PHONE, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import { PROBATE_FEES } from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/probate-checklist-queensland`;

const TITLE = "Probate Checklist for Executors in Queensland — Evidence, Affidavits & Requisitions";
const DESCRIPTION = `An executor's probate checklist for Queensland: documents to gather, the affidavits the Supreme Court needs, and how to answer a capacity requisition. Fixed-fee probate from ${PROBATE_FEES.grant}. Call ${FIRM.phone}.`;

const ANSWER =
  "To apply for probate in Queensland, the executor gathers the original will, the death certificate and a full picture of the assets and debts, publishes a notice of intention to apply on the Queensland Law Society website, waits at least 14 days, then files an application with the Supreme Court supported by affidavits. " +
  "The core affidavits cover the executor's details and the will, publication and service of the notice, the assets and liabilities, and (where needed) the attesting witness's account of how the will was signed. " +
  "If the Registrar has doubts, for example because the will was signed close to death or the cause of death suggests dementia or delirium, a requisition is issued asking for further evidence of the will-maker's testamentary capacity. " +
  "That is answered with affidavits from the lawyer who took instructions, the attesting witnesses and, ideally, the treating doctor, together with the will file notes and relevant medical records.";

type Group = { heading: string; intro: string; items: string[] };

const GROUPS: Group[] = [
  {
    heading: "1. Documents to gather first",
    intro: "Get these together before anything is filed. The Court needs originals where stated.",
    items: [
      "The original will and any codicils — not a copy. Check staples and clips have not been removed.",
      "Death certificate from the Registry of Births, Deaths and Marriages (an interim certificate is not enough).",
      "Your own photo ID and proof of address as executor.",
      "Contact details and ages of every beneficiary named in the will.",
      "Any earlier wills you know of, and the details of any person who may say they were left out.",
      "The will file from the solicitor who prepared the will, including their instruction notes.",
    ],
  },
  {
    heading: "2. Assets and debts",
    intro: "The Court wants a genuine estimate, with values at the date of death.",
    items: [
      "Bank, term deposit and share statements with balances at the date of death.",
      "Title searches for any Queensland real estate, and a market appraisal or valuation.",
      "Vehicles, boats and valuable personal items with an approximate value.",
      "Debts: mortgage, credit cards, personal loans, funeral account, outstanding rates and tax.",
      "Superannuation and life insurance details (usually outside the estate, but listed for context).",
      "Anything jointly owned — noted separately, as it normally passes to the survivor.",
    ],
  },
  {
    heading: "3. Notice of intention to apply",
    intro: "A step many executors miss, and it cannot be rushed.",
    items: [
      "Publish the notice of intention to apply for a grant on the Queensland Law Society website.",
      "Keep a copy of the published notice and the date it went up.",
      "Serve a copy on the Public Trustee if required (for example, where a beneficiary is a minor or lacks capacity).",
      "Wait at least 14 days after publication before filing the application.",
      "Search the Supreme Court for any caveat lodged against a grant.",
    ],
  },
  {
    heading: "4. Affidavits for the Supreme Court",
    intro: "Each affidavit is sworn or affirmed before a lawyer or JP, with exhibits attached.",
    items: [
      "Application for grant (Form 107) naming the executor and the will.",
      "Affidavit of executor exhibiting the original will and death certificate, and confirming searches and identity.",
      "Affidavit of publication and service of the notice of intention to apply.",
      "Statement of assets and liabilities, with approximate date-of-death values.",
      "Affidavit of an attesting witness, where the will is irregular, damaged, altered or the signing is in question.",
      "Affidavit explaining any marks, alterations, missing attachments or pin holes on the original will.",
      "Draft grant of probate for the Registrar to seal.",
    ],
  },
  {
    heading: "5. If a capacity requisition arrives",
    intro:
      "The Registrar issues a requisition when something on the file raises doubt — often a will signed within weeks of death, or a cause of death such as dementia, a brain tumour or delirium.",
    items: [
      "Read the requisition carefully and note exactly what the Registrar is asking for. Answer that, not more.",
      "Request the complete will file from the solicitor who took the instructions, including handwritten notes and emails.",
      "Obtain an affidavit from the lawyer who took instructions: who attended, how long, what questions were asked and how the will-maker answered.",
      "Obtain affidavits from both attesting witnesses describing the will-maker's behaviour and understanding at signing.",
      "Request medical records from the GP, hospital and any aged care facility for the months around the will.",
      "Ask the treating doctor for a report or affidavit addressing the Banks v Goodfellow test at the date of the will.",
      "Gather evidence from family, friends or carers who saw the will-maker around that time, where it helps.",
      "Explain the will: why it differs from any earlier will, and whether the change was discussed before.",
      "File the response with a covering letter cross-referencing each point in the requisition.",
      "Keep beneficiaries informed. A requisition is not a finding the will is invalid.",
    ],
  },
  {
    heading: "6. After the grant",
    intro: "The grant is the key that lets you deal with the assets.",
    items: [
      "Send certified copies of the grant to banks, share registries and Titles Queensland.",
      "Wait six months before distributing if there is any risk of a family provision claim.",
      "Pay debts, lodge the final tax return and any trust returns.",
      "Keep a written record of every receipt and payment, and prepare estate accounts for beneficiaries.",
    ],
  },
];

const EVIDENCE_AT_SIGNING = [
  "Detailed file notes of the instructions meeting, recorded in the will-maker's own words.",
  "Seeing the will-maker alone, without the beneficiaries in the room.",
  "Open questions about their assets, family and why they want the changes.",
  "A note of any illness, medication, hearing or eyesight issues on the day.",
  "A doctor's capacity assessment close to signing where there is any doubt (the 'golden rule').",
  "Witnesses who can later give a clear account of the signing.",
];

const FAQS = [
  {
    q: "How long does probate take in Queensland?",
    a: "A straightforward application is usually granted within four to eight weeks of filing, after the 14-day notice period. A requisition adds time — often several weeks — depending on how quickly medical records and witness affidavits can be obtained.",
  },
  {
    q: "What is a requisition?",
    a: "It is a written request from the Supreme Court Registrar for more information or evidence before a grant is made. It can be as minor as a typo in the name, or as serious as asking for evidence that the will-maker had testamentary capacity.",
  },
  {
    q: "Why would the Registrar question capacity?",
    a: "Common triggers are a will signed shortly before death, a cause of death on the certificate that affects the mind such as dementia or a brain tumour, a will that departs sharply from earlier wills, or signs that the will-maker was frail or in hospital when they signed.",
  },
  {
    q: "What is the Banks v Goodfellow test?",
    a: "It is the long-standing test for testamentary capacity. The will-maker must understand they are making a will and its effect, have a general understanding of what they own, be aware of the people who might expect to benefit, and not be affected by a disorder of the mind that distorts their judgment.",
  },
  {
    q: "Does a requisition mean the will is invalid?",
    a: "No. It means the Registrar needs more evidence before being satisfied. Most capacity requisitions are resolved with good affidavit evidence. If the evidence is contested or unclear, the matter may need to go before a judge.",
  },
  {
    q: "Can Coastside Law take over an application that has already been requisitioned?",
    a: "Yes. We regularly step in after a requisition has issued, collect the evidence, prepare the affidavits and respond to the Registrar. We quote a fixed fee for the response once we have seen the requisition.",
  },
];

export const Route = createFileRoute("/probate-checklist-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Probate checklist for executors in Queensland",
          description: ANSWER,
          step: GROUPS.map((g, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: g.heading.replace(/^\d+\.\s*/, ""),
            text: g.items.join(" "),
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Executors", item: `${SITE}/executor-queensland` },
            { "@type": "ListItem", position: 3, name: "Probate checklist", item: URL },
          ],
        }),
      },
    ],
  }),
  component: ProbateChecklist,
});

function ProbateChecklist() {
  return (
    <>
      <PageHero
        eyebrow="Executor checklist"
        title="Applying for probate in Queensland: the executor's checklist"
        lead="What to gather, which affidavits the Supreme Court expects, and what to do if the Registrar sends a requisition about the will-maker's capacity."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-primary/40 bg-primary/5 p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Short answer
          </p>
          <p className="mt-3 text-base leading-relaxed text-foreground">{ANSWER}</p>
        </div>

        {GROUPS.map((g) => (
          <div key={g.heading} className="mt-14">
            <h2 className="font-serif text-3xl text-foreground">{g.heading}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{g.intro}</p>
            <ul className="mt-5 grid gap-2.5">
              {g.items.map((item) => (
                <li
                  key={item}
                  className="flex gap-3 rounded-sm border border-border bg-card p-4 text-sm leading-relaxed text-foreground"
                >
                  <span
                    aria-hidden
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-2 border-primary"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h2 className="font-serif text-2xl text-foreground">
            For will-makers: evidence that helps your executor later
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Most capacity requisitions are answered from what was recorded when the will was signed.
            When we prepare a will, particularly for someone elderly or unwell, we make sure the file
            has:
          </p>
          <ul className="mt-4 space-y-2">
            {EVIDENCE_AT_SIGNING.map((e) => (
              <li key={e} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">How Coastside Law can help</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          We act for executors across the Sunshine Coast, Moreton Bay and remotely throughout
          Queensland. Straightforward grants are fixed-fee from {PROBATE_FEES.grant} including GST.
          If your application has been requisitioned, send us the requisition and we will tell you
          what evidence is needed and what it will cost to respond.
        </p>
        <ul className="mt-6 grid gap-3">
          <li>
            <Link
              to="/guides/$slug"
              params={{ slug: "probate-requisition-capacity-queensland" }}
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                Capacity requisitions explained in depth
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                The Banks v Goodfellow test and how lawyers respond to the Registrar.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/executor-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">Executor help and fees</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                What we do for executors and what it costs.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/what-happens-when-someone-dies-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                What happens when someone dies in Queensland
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                The full sequence, from the funeral to final distribution.
              </span>
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will help you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Coastside Law, {FIRM_ADDRESS} and {PENINSULA_ADDRESS}. Call {FIRM.phone} or{" "}
          {MICHAEL_PHONE} (Michael Klein). This checklist is general information about Queensland
          law, not advice on your estate.
        </p>

        <div className="mt-12">
          <EnquiryForm heading="Get help with a probate application or requisition" />
        </div>
      </section>

      <CtaBand
        title="Received a requisition from the Supreme Court?"
        body="Send it to us. We will explain what the Registrar wants and quote a fixed fee to respond."
      />
    </>
  );
}
